import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';

import {
  makeStyles,
  Typography,
  Button,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';

import { Toast } from '../../Toast/Toast';
import { validateEmail } from '../../Utils/helper';
import {
  SingleDialog,
  DialogHeader,
  DialogActions,
  DialogContent,
} from '../../CustomDialogs';
import CustomerFields from './CustomerFields';

import { getLaborClasses } from '../../../actions/laborClass/labor-class-actions';
import { addEditCustomer } from '../../../api/customer/customer-api';

const useStyles = makeStyles((theme) => ({
  dialog__repair__times: {
    width: '50%',
    height: '90vh',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '98%',
      width: '98%',
    },
  },
  heading: {
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '34px',
    color: '#000',
  },
  header__style: {
    padding: '1rem',
    background: '#fcfcfc',
  },
  body__style: {
    padding: '1rem 1rem 3rem 1rem',
    height: 'calc(100% - 120px)',
    overflow: 'auto',
    background: '#fcfcfc',
  },
  back__btn: {
    fontSize: '13px',
    borderRadius: '100px',
    height: '38px',
    width: '110px',
    textTransform: 'none',
    fontWeight: '500',
    color: 'rgb(45, 68, 87)',
    boxShadow: 'none',
    background: 'rgba(109, 125, 139, 0.2)',
    '&:hover': {
      boxShadow: 'none',
      background: 'rgba(109, 125, 139, 0.2)',
    },
  },
  save__btn: {
    background: '#FF6019',
    borderRadius: '100px',
    fontWeight: '500',
    fontSize: '12px',
    lineHeight: '14px',
    color: '#fff',
    padding: '.7rem 2rem',
    textTransform: 'capitalize',
    '&:hover': {
      background: '#FF6019',
    },
  },
  spinner: {
    marginLeft: '10px',
    color: 'var(--color-secondary)',
  },
  disable__btn: {
    background: '#ECECEE',
  },
  text__exempt__main: {
    display: 'flex',
    alignItems: 'center',
  },
  text__exempt: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#545b7d',
    lineHeight: '15px',
    paddingRight: '1rem',
  },
  add__vehicle__btn: {
    margin: '1rem 0',
    background: 'rgba(105, 33, 196, 0.18)',
    color: 'rgba(105, 33, 196, 1)',
    width: '150px !important',
    fontSize: '13px',
    borderRadius: '100px !important',
    height: '40px !important',
    textTransform: 'none',
    fontWeight: '500 !important',
    boxShadow: 'none',
    '&:hover': {
      background: 'rgba(105, 33, 196, 0.18)',
      boxShadow: 'none',
    },
  },
}));

const CustomerPopup = ({
  openCustomerPopup,
  setOpenCustomerPopup,
  customerData = '',
  isViewOnly = false,
  setCustomerDetails,
  selectedVehicleId = null,
  isAdditionalInfo = false,
  handleAdditionalInfoSave,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  let customer = '';

  if (customerData?.id) {
    // edit case
    customer = customerData;
  } else if (isAdditionalInfo) {
    customer = customerData;
  }

  const { laborClasses } = useSelector((state) => state?.laborClass);

  // Errors/validations
  const [errors, setErrors] = useState();

  // Labor Rate
  const [laborClassOptions, setLaborClassOptions] = useState([]);
  const [laborClassLabel, setLaborClassLabel] = useState('Select Labor Rate');

  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // get labor classess

    if (laborClasses?.length === 0) {
      dispatch(getLaborClasses());
    }

    // set customer values for update

    if (customer?.id || isAdditionalInfo) {
      const {
        id,
        labour_class: labourClass = null,
        name = null,
        email = null,
        phone = null,
        address = null,
        company = null,
        referalSource = null,
        tags = [],
        notes = null,
        usDotNumber = null,
        pstNumber = null,
        isCustomer = null,
        isTaxExempt,
        additionalEmails = '',
      } = customer;

      if (labourClass?.className) {
        setLaborClassLabel(labourClass.className);
      }

      setFields({
        ...fields,
        id,
        name,
        phone,
        email,
        address,
        company,
        referalSource,
        tags,
        notes,
        labourClassId: labourClass?.id || null,
        usDotNumber,
        pstNumber,
        isCustomer,
        isTaxExempt,
        additionalEmails,
      });
    }
  }, [customer]);

  // labor classes list
  useEffect(() => {
    if (laborClasses?.length) {
      const options = [];
      laborClasses.map((lc) => {
        if (!lc.classStatus) return;
        options.push({
          name: `${lc.className} ${
            lc.classRate ? `( $${lc.classRate} )` : '( $0 )'
          } `,
          value: lc.id,
        });
      });
      setLaborClassOptions(options);
    }
  }, [laborClasses]);

  const isScroll = useRef();

  const handleChangeFields = (e) => {
    const { name, value } = e.target;
    setFields({ ...fields, [name]: value });

    if (name === 'name' || name === 'phone' || name === 'email') {
      setErrors({
        ...errors,
        [name]: false,
      });
    }
  };

  // handle validations
  const handleValidations = () => {
    const errorObj = {};

    let isValid = true;

    const { name, phone, email } = fields;

    if (!name?.trim()) {
      errorObj.name = true;
      isValid = false;
    }
    if (!phone || phone?.length < 11) {
      errorObj.phone = true;
      isValid = false;
    }
    if (email && !validateEmail(email)) {
      errorObj.email = true;
      isValid = false;
    }

    setErrors({
      ...errors,
      ...errorObj,
    });

    return isValid;
  };

  const clearStates = () => {
    setOpenCustomerPopup(false);
    setErrors();
    setFields();
    setLaborClassOptions([]);
  };

  // handle save

  const handleSave = async () => {
    if (handleValidations()) {
      if (fields?.phone?.[0] !== '+') {
        fields.phone = '+' + fields.phone;
      }

      const {
        id = null,
        referalSource = null,
        labourClassId = null,
        tags = [],
        notes = null,
        name = null,
        email = null,
        phone = null,
        address = null,
        company = null,
        usDotNumber = null,
        pstNumber = null,
        isTaxExempt,
      } = fields;

      if (isAdditionalInfo) {
        handleAdditionalInfoSave({
          referalSource,
          labourClassId,
          tags,
          notes,
          name,
          email,
          phone,
          address,
          company,
          usDotNumber,
          pstNumber,
          isTaxExempt,
          additionalEmails: fields?.additionalEmails,
        });
        clearStates();
      } else {
        setLoading(true);

        try {
          const { data } = await addEditCustomer({
            customer: {
              id,
              referalSource,
              labourClassId,
              tags,
              notes,
              name,
              email,
              phone,
              address,
              company,
              usDotNumber,
              pstNumber,
              isTaxExempt,
              additionalEmails: fields?.additionalEmails,
            },
            vehicleId: selectedVehicleId,
          });
          setCustomerDetails({ ...data?.data, isCustomer: true });
          clearStates();
        } catch (err) {
          const _error =
            (err?.response && err?.response?.data?.message
              ? err.response.data.message
              : err?.message) || 'Error';
          Toast('error', _error);
        }
        setLoading(false);
      }
    }
  };

  const handleSelectCutomerFromSearch = (_customer) => {
    setFields({
      ...fields,
      ..._customer,
    });
  };

  return (
    <div>
      <SingleDialog
        toggle={openCustomerPopup}
        styles={classes.dialog__repair__times}
      >
        <DialogHeader headerStyle={classes.header__style}>
          <Typography className={classes.heading}>
            {fields?.isCustomer ? 'Edit ' : 'Add '}
            Customer Details
          </Typography>
        </DialogHeader>
        <DialogContent mainStyles={classes.body__style}>
          <Grid
            item
            xs={12}
            ref={isScroll}
            className={classes.labor__guide__data}
          >
            <CustomerFields
              handleChangeFields={handleChangeFields}
              fields={fields}
              laborClassOptions={laborClassOptions}
              laborClassLabel={laborClassLabel}
              setLaborClassLabel={setLaborClassLabel}
              errors={errors}
              isViewOnly={isViewOnly}
              handleSelectCutomerFromSearch={handleSelectCutomerFromSearch}
              isAdditionalInfo={isAdditionalInfo}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.back__btn}
            onClick={clearStates}
            disabled={loading}
          >
            Cancel
          </Button>
          {!isViewOnly && (
            <Button
              className={clsx(
                classes.save__btn,
                loading && classes.disable__btn,
              )}
              onClick={handleSave}
              disabled={loading}
            >
              Save Changes
              {loading && (
                <CircularProgress className={classes.spinner} size={15} />
              )}
            </Button>
          )}
        </DialogActions>
      </SingleDialog>
    </div>
  );
};
export default CustomerPopup;
